import type { ReactNode } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { ItemPlan } from './itemPlan';

interface ShortfallPopoverProps {
  plan: ItemPlan;
  /** The badge that opens it. */
  children: ReactNode;
}

/**
 * Why an item cannot be covered, opened by hovering its `Short` / `Out of stock` badge
 * (14ykpqtnxbv §7).
 *
 * Structured figures rather than a line of tooltip text: the person needs to see WHICH MRS line loses
 * out, because the lines are different jobs and the one that goes short is a decision they may want to
 * revisit. Read-only — every control stays on the card.
 *
 * Hover-triggered via Tooltip rather than click-triggered Popover: Radix gives collision-aware flipping
 * for free (the design hand-rolled a "flip above when under ~215px remains below" check), plus keyboard
 * focus and a portal that escapes the cart's own scroll container.
 */
export function ShortfallPopover({ plan, children }: ShortfallPopoverProps) {
  const shortLines = plan.lines.filter((l) => l.short);
  const nothingOnHand = plan.known && (plan.effectiveAvailable ?? 0) <= 0;

  // The card's own ground stays normal; the flag lives here and on the badge (owner ruling 4,
  // refined in review). Body copy is FOREGROUND ink, not amber — amber text on an amber tint fails
  // 4.5:1 in all three themes.
  // 14ykpqtv7nd — "nothing on hand" was untrue when the shelf has stock that other picklists hold: say which.
  const allHeldElsewhere = nothingOnHand && (plan.onHand ?? 0) > 0 && plan.committed > 0;
  const reason = allHeldElsewhere
    ? 'Everything on hand is held by other open picklists. These lines stay for the next picklist.'
    : nothingOnHand
    ? 'Nothing on hand to allocate. These lines stay for the next picklist.'
    : shortLines.length > 1 || plan.lines.length > 1
      ? `Not enough stock for the ${plan.lines.length} lines together. They take what is on the shelf, in line order; the picklist is still created and the balance waits for stock.`
      : 'Not enough stock for this line. It takes what is on the shelf; the picklist is still created and the balance waits for stock.';

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent
          side="bottom"
          align="start"
          className="w-[286px] max-w-[92vw] rounded-[10px] border-border bg-card p-0 text-card-foreground shadow-lg"
        >
          <div className="border-b border-border px-3 py-2">
            <p className="text-xs font-semibold">
              {nothingOnHand ? 'Out of stock' : `Short ${plan.shortfall} ${plan.unitOfMeasure}`}
            </p>
            <p className="font-mono text-[11px] text-muted-foreground">{plan.itemCode}</p>
          </div>

          {/* Paired figures. Committed is NAMED rather than silently folded into "on hand" (owner
              ruling 2026-09-18): a held unit is not gone — the picklist holding it can be cancelled
              or short-picked — so subtracting it invisibly understates just as raw on-hand overstates. */}
          <dl className="grid grid-cols-2 gap-x-3 gap-y-1 px-3 py-2 text-xs">
            <dt className="text-muted-foreground">On hand</dt>
            <dd className="text-right font-medium tabular-nums">{plan.onHand ?? '—'}</dd>

            {plan.committed > 0 && (
              <>
                <dt className="text-muted-foreground">Held by other picklists</dt>
                <dd className="text-right font-medium tabular-nums text-warning">
                  −{plan.committed}
                </dd>
              </>
            )}

            <dt className="text-muted-foreground">Wanted</dt>
            <dd className="text-right font-medium tabular-nums">{plan.wanted}</dd>
          </dl>

          <div className="border-t border-border px-3 py-2">
            <p className="mb-1 text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
              What each line gets
            </p>
            <ul className="space-y-0.5 text-xs">
              {plan.lines.map((line) => (
                <li key={line.key} className="flex items-baseline justify-between gap-2">
                  <span className="truncate font-mono text-[11px] text-muted-foreground">
                    {line.item.mrsNumber}
                  </span>
                  <span
                    className={`shrink-0 tabular-nums ${line.short ? 'font-semibold text-warning' : ''}`}
                  >
                    {line.cap} of {line.wanted}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <p className="border-t border-border px-3 py-2 text-[11px] leading-snug text-foreground">
            {reason}
          </p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
